import {
  date,
  isNonNullable,
  isNullable,
  serviceLogFormat,
} from '@ipfabric/utils';
import {
  JOB_ACTIONS,
  JOB_QUEUED,
  JOB_STARTED,
  JOB_STATUSES,
  JOB_UPDATED,
} from 'containers/App/Header/JobManagement/constants';
import { right } from 'fp-ts/lib/Either';
import { Map as ImmutableMap, List, Record, fromJS } from 'immutable';
import get from 'lodash/get';
import { pipe } from 'ramda';
import { generateGuid } from 'utils/guid';
import updateImmutableData from 'utils/updateImmutableData';
import {
  BANDWIDTH_MSG_RECEIVED,
  CHANGE_SNAPSHOT,
  CLEAR_DEVICES,
  CLEAR_INVENTORY,
  CLEAR_LOGS,
  CLEAR_STATE,
  CLEAR_TABS,
  CLONE_SNAPSHOT,
  CLOSE_HOSTNAME,
  CLOSE_SETTINGS_NO_PASSWORDS,
  DELETE_DEVICE,
  DELETE_DEVICE_FINISH,
  DELETE_DEVICE_STOP,
  DELETE_SNAPSHOT,
  DELETE_SNAPSHOT_FINISH,
  DISABLED_POST_DISCOVERY_ACTIONS_CHANGE,
  DISABLE_START_BTN,
  DISCOVERY_PROCESS_START,
  DISCOVERY_TASKS_CHANGE,
  DOWNLOAD_LOG,
  ENABLE_START_BTN,
  FETCH_DISABLED_POST_DISCOVERY_ACTIONS,
  FETCH_DISCOVERY_TASKS,
  FETCH_HOSTNAME,
  FETCH_LOGS,
  FETCH_SITE_SEPARATION,
  FETCH_SNAPSHOT_DATA,
  FETCH_SNAPSHOT_MANAGEMENT,
  LOADING_STARTED,
  LOAD_SNAPSHOT,
  LOAD_SNAPSHOT_FINISH,
  LOCK_SNAPSHOT,
  NAME_CHANGE,
  NETWORKER_CONSOLE_CLEAR,
  NETWORKER_LOGS_RECEIVED,
  NEW_DEVICE_SAVED,
  NOTE_CHANGE,
  OPEN_NEW_SETTINGS,
  OPEN_SETTINGS,
  PAGE_URL,
  SAVE_DEVICES,
  SAVE_INVALID_PASSWORDS,
  SERVER_TS_RECEIVED,
  SET_ACTIVE_MENU_TAB,
  SET_SHAPER_SETTINGS,
  SHOW_ADD_LOADER,
  SHOW_REFRESH_LOADER,
  SNAPSHOT_INFO_RECEIVED,
  SNAPSHOT_PREVENT_DEVICE_LOAD,
  SNAPSHOT_STATUS_RECEIVED,
  TASKER_CONSOLE_CLEAR,
  TASKER_LOGS_RECEIVED,
  UNLOAD_SNAPSHOT,
  UNLOAD_SNAPSHOT_FINISH,
  UNLOCK_SNAPSHOT,
  UPLOAD_SNAPSHOT,
  WORKER_CONSOLE_CLEAR,
  WORKER_LAST_COMMUNICATION,
  WORKER_LOGS_RECEIVED,
} from './constants';
import { updateSnapshotState } from './helpers';

const Messages = new Record({
  tasker: new List(),
  worker: new List(),
  netWorker: new List(),
});

function clearTabs(state) {
  return state
    .set('selectedSite', false)
    .set('inProgressDevices', new List())
    .set('devices', new List())
    .set('messages', new Messages());
}

function initBandwidthGrafData(count) {
  const result = [];
  let now = date.getTs() - count * 1000;
  let i = count;
  while (i > 0) {
    result.push({
      rx: '0',
      tx: '0',
      ts: now,
    });
    i -= 1;
    now += 1000;
  }
  return List(result);
}

const setSnapshotsLoadingState = (loading) => (snapshots) =>
  snapshots.map((snapshot) =>
    snapshot.has('loading') ? snapshot.set('loading', loading) : snapshot,
  );

const InitialState = Record({
  allSnapshotsSites: new List(),
  cloningSnapshot: new List(),
  dataLoading: true,
  deletingSnapshot: new List(),
  loadingSnapshot: new List(),
  lockingSnapshot: new List(),
  savingName: false,
  savingNote: false,
  selectedSite: false,
  selectedSnapshot: null,
  snapshots: new List(),
  unloadingSnapshot: new List(),
  unlockingSnapshot: new List(),
  uploadDialogueError: false,
  uploadingSnapshot: false,
  hostname: null,
  activeMenuTab: 'snapshots_upload',
  deleteDevicesSnapshotId: null,
  refreshLoader: new ImmutableMap(),
  addLoader: new ImmutableMap(),
  fetchDevicesLoader: new ImmutableMap(),
  tasksSelectedRows: new List(),
  tasksSelectedAllRows: false,
  settingsLoading: false,
  logsLoading: false,
  requestData: null,
  invalidPasswords: new ImmutableMap(),
  siteSeparation: null,
  discoveryTasks: [],
  disabledPostDiscoveryActions: right([]),

  bandwidth: initBandwidthGrafData(60),
  devices: new List(),
  inProgressDevices: new List(),
  maxBandwidth: null,
  messages: new Messages(),
  disableStartButton: false,
  downloadingLogKeys: new List(),
});

const initialState = new InitialState();

const removeFromList = (id, items) => {
  if (Array.isArray(id)) {
    return items.filter((item) => id.indexOf(item) === -1);
  }
  const foundIndex = items.findIndex((item) => item === id);
  if (foundIndex > -1) {
    return items.delete(foundIndex);
  }
  return items;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
export default function snapshotsReducer(
  state = initialState,
  action = undefined,
) {
  const updateStateMessages = (payload, key, s) => {
    return payload.reduce((acc, val) => {
      const message = serviceLogFormat(
        val.message.level,
        val.message.message,
        val.message.label ?? 'default',
        val.message.timestamp,
      );
      return acc.updateIn(['messages', key], (item) => item.push(message));
    }, s);
  };
  switch (action.type) {
    case CLEAR_STATE: {
      return initialState
        .set('snapshots', state.get('snapshots'))
        .set('selectedSite', state.get('selectedSite'))
        .set('selectedSnapshot', state.get('selectedSnapshot'));
    }
    case CHANGE_SNAPSHOT: {
      const value = get(action, 'payload.value', null);
      if (value === 'new') {
        return state;
      }
      return state.set('selectedSnapshot', value);
    }
    case `${FETCH_SNAPSHOT_MANAGEMENT}_SUCCESS`: {
      const { data = [] } = action.payload;

      return pipe(
        (s) => s.set('dataLoading', false),
        // After reconnecting to socket, set loading snapshot in case loading has already stopped
        (s) => s.updateIn(['snapshots'], setSnapshotsLoadingState(false)),
        (s) =>
          updateImmutableData(
            'snapshots',
            s,
            data.map((snapshot) =>
              snapshot.loading !== undefined
                ? { ...snapshot, loading: false }
                : snapshot,
            ),
          ),
      )(state);
    }
    case CLEAR_INVENTORY: {
      const { snapshotId } = action.payload;
      return pipe(
        (s) => s.set('devices', new List()),
        (s) => {
          if (isNonNullable(snapshotId)) {
            const foundIndex = s
              .get('snapshots')
              .findIndex((snapshot) => snapshot.get('id') === snapshotId);
            if (foundIndex > -1) {
              return s.deleteIn(['snapshots', foundIndex, 'preventDeviceLoad']);
            }
          }
          return s;
        },
      )(state);
    }
    case CLEAR_DEVICES:
      return state
        .set('devices', new List())
        .set('inProgressDevices', new List());
    case `${FETCH_SNAPSHOT_DATA}_SUCCESS`: {
      const { snapshotId } = action.payload;
      const data = get(action, ['payload', 'snapshot'], {});
      if (isNullable(snapshotId)) {
        return state;
      }
      const snapshots = state.get('snapshots');
      const foundIndex = snapshots.findIndex(
        (snapshot) => snapshot.get('id') === snapshotId,
      );
      if (foundIndex > -1) {
        const snapshot = snapshots.get(foundIndex);
        const immutableData = fromJS(data);
        const equals = snapshot.equals(immutableData);
        if (equals) {
          return state;
        }
        return state.setIn(['snapshots', foundIndex], immutableData);
      }
      return state;
    }
    case `${FETCH_SNAPSHOT_MANAGEMENT}_ERROR`:
      return state.set('dataLoading', false);
    case `${LOAD_SNAPSHOT}_START`: {
      const { idList } = action.payload;
      return idList.reduce(
        (s, id) => {
          const foundIndex = s
            .get('snapshots')
            .findIndex(
              (snapshot) =>
                snapshot.get('id') === id && !snapshot.get('locked'),
            );
          if (foundIndex > -1) {
            return s
              .setIn(['snapshots', foundIndex, 'vTask'], new ImmutableMap())
              .setIn(
                ['snapshots', foundIndex, 'vTraceCache'],
                new ImmutableMap(),
              )
              .setIn(['snapshots', foundIndex, 'vCollect'], new ImmutableMap())
              .setIn(['snapshots', foundIndex, 'errors'], new List())
              .set('messages', new Messages());
          }
          return s;
        },
        state.update('loadingSnapshot', (loadingSnapshot) =>
          loadingSnapshot.concat(new List(action.payload.idList)),
        ),
      );
    }
    case `${LOAD_SNAPSHOT_FINISH}_SUCCESS`:
    case `${LOAD_SNAPSHOT}_ERROR`:
      return state
        .update('loadingSnapshot', (items) =>
          removeFromList(action.payload.idList, items),
        )
        .set('uploadingSnapshot', false);
    case `${UNLOAD_SNAPSHOT}_START`:
      return state.update('unloadingSnapshot', (unloadingSnapshot) =>
        unloadingSnapshot.concat(new List(action.payload.id)),
      );
    case `${UNLOAD_SNAPSHOT_FINISH}_SUCCESS`:
    case `${UNLOAD_SNAPSHOT}_ERROR`:
      return state.update('unloadingSnapshot', (items) =>
        removeFromList(action.payload.id, items),
      );
    case `${DELETE_SNAPSHOT}_START`: {
      const { id } = action.payload;
      return state.update('deletingSnapshot', (deletingSnapshot) =>
        deletingSnapshot.concat(new List(id)),
      );
    }
    case `${DELETE_SNAPSHOT_FINISH}_SUCCESS`:
    case `${DELETE_SNAPSHOT}_ERROR`:
      return state.update('deletingSnapshot', (items) =>
        removeFromList(action.payload.id, items),
      );
    case `${CLONE_SNAPSHOT}_ERROR`:
      return state.update('cloningSnapshot', (items) =>
        removeFromList(action.payload.id, items),
      );
    case `${UPLOAD_SNAPSHOT}_START`:
      return state
        .set('uploadingSnapshot', true)
        .set('uploadDialogueError', false);
    case `${UPLOAD_SNAPSHOT}_SUCCESS`:
      return state.set('uploadDialogueError', false);
    case `${UPLOAD_SNAPSHOT}_STOP`:
    case `${UPLOAD_SNAPSHOT}_ERROR`:
      return state
        .set('uploadingSnapshot', false)
        .set('uploadDialogueError', true);
    case JOB_STARTED: {
      if (!Array.isArray(action.payload)) {
        return state;
      }
      return action.payload.reduce((acc, job) => {
        const { snapshot, status, action: jobAction } = job;
        return pipe(
          (s) => {
            if (
              jobAction === JOB_ACTIONS.snapshotLoad &&
              status === JOB_STATUSES.running
            ) {
              const foundIndex = s
                .get('snapshots')
                .findIndex((item) => item.get('id') === snapshot);
              if (foundIndex > -1) {
                return pipe(
                  (updatedState) =>
                    updatedState.setIn(
                      ['snapshots', foundIndex, 'totalDevCount'],
                      0,
                    ),
                  (updatedState) =>
                    updatedState.setIn(
                      ['snapshots', foundIndex, 'licensedDevCount'],
                      0,
                    ),
                  (updatedState) => {
                    if (
                      window.location.pathname.startsWith(
                        `${PAGE_URL}/${snapshot}`,
                      )
                    ) {
                      return updatedState.setIn(
                        ['snapshots', foundIndex, 'preventDeviceLoad'],
                        true,
                      );
                    }
                    return updatedState;
                  },
                )(s);
              }
            }
            return s;
          },
          (s) => {
            if (
              status === JOB_STATUSES.running &&
              get(job, 'args.action') === 'refresh'
            ) {
              const snapshotId = get(job, 'snapshot');
              return s.setIn(['refreshLoader', snapshotId], true);
            }
            return s;
          },
          (s) => {
            if (
              status === JOB_STATUSES.running &&
              get(job, 'args.action') === 'add'
            ) {
              const snapshotId = get(job, 'snapshot');
              return s.setIn(['addLoader', snapshotId], true);
            }
            return s;
          },
        )(acc);
      }, state);
    }
    case JOB_QUEUED:
    case JOB_UPDATED: {
      if (!Array.isArray(action.payload)) {
        return state;
      }
      return action.payload.reduce((acc, job) => {
        const { snapshot, status, action: jobAction } = job;
        return pipe(
          (s) => {
            if (
              ![
                JOB_STATUSES.running,
                JOB_STATUSES.done,
                JOB_STATUSES.stopping,
                JOB_STATUSES.stopped,
              ].includes(status)
            ) {
              return s;
            }
            const foundIndex = s
              .get('snapshots')
              .findIndex((item) => item.get('id') === snapshot);
            if (foundIndex > -1) {
              return s.updateIn(['snapshots', foundIndex], (item) => {
                return item
                  .set(
                    'state',
                    updateSnapshotState(jobAction, status, item.get('state')),
                  )
                  .set(
                    'running',
                    ![
                      JOB_ACTIONS.snapshotDownload,
                      JOB_ACTIONS.techsupport,
                      JOB_ACTIONS.configurationExport,
                      JOB_ACTIONS.configurationImport,
                    ].includes(jobAction) &&
                      [JOB_STATUSES.running, JOB_STATUSES.stopping].includes(
                        status,
                      ),
                  );
              });
            }
            return s;
          },
          (s) => {
            if (
              jobAction !== JOB_ACTIONS.snapshotDelete &&
              ![
                JOB_STATUSES.done,
                JOB_STATUSES.cancelled,
                JOB_STATUSES.error,
              ].includes(status)
            ) {
              return s;
            }
            const index = s.get('deletingSnapshot').indexOf(snapshot);
            if (index > -1) {
              return s.update('deletingSnapshot', (deletingSnapshot) =>
                deletingSnapshot.delete(index),
              );
            }
            return s;
          },
          (s) => {
            if (
              jobAction !== JOB_ACTIONS.snapshotLoad &&
              ![
                JOB_STATUSES.done,
                JOB_STATUSES.cancelled,
                JOB_STATUSES.error,
              ].includes(status)
            ) {
              return s;
            }
            const index = s.get('loadingSnapshot').indexOf(snapshot);
            if (index > -1) {
              return s
                .update('loadingSnapshot', (loadingSnapshot) =>
                  loadingSnapshot.delete(index),
                )
                .set('uploadingSnapshot', false)
                .set('uploadDialogueError', true);
            }
            return s
              .set('uploadingSnapshot', false)
              .set('uploadDialogueError', true);
          },
          (s) => {
            if (
              jobAction !== JOB_ACTIONS.snapshotClone &&
              ![
                JOB_STATUSES.done,
                JOB_STATUSES.cancelled,
                JOB_STATUSES.error,
              ].includes(status)
            ) {
              return s;
            }
            const index = s.get('cloningSnapshot').indexOf(snapshot);
            if (index > -1) {
              return s.update('cloningSnapshot', (cloningSnapshot) =>
                cloningSnapshot.delete(index),
              );
            }
            return s;
          },
          (s) => {
            if (
              jobAction !== JOB_ACTIONS.snapshotUnload &&
              ![
                JOB_STATUSES.done,
                JOB_STATUSES.cancelled,
                JOB_STATUSES.error,
              ].includes(status)
            ) {
              return s;
            }
            const index = s.get('unloadingSnapshot').indexOf(snapshot);
            if (index > -1) {
              return s.update('unloadingSnapshot', (unloadingSnapshot) =>
                unloadingSnapshot.delete(index),
              );
            }
            return s;
          },
          (s) => {
            if (
              status === JOB_STATUSES.cancelled &&
              get(job, 'args.action') === 'refresh'
            ) {
              const snapshotId = get(job, 'args.vCollectKey');
              return s.setIn(['refreshLoader', snapshotId], false);
            }
            return s;
          },
          (s) => {
            if (
              status === JOB_STATUSES.cancelled &&
              get(job, 'args.action') === 'add'
            ) {
              const snapshotId = get(job, 'args.vCollectKey');
              return s.setIn(['addLoader', snapshotId], false);
            }
            return s;
          },
        )(acc);
      }, state);
    }
    case `${NAME_CHANGE}_START`: {
      return state.set('savingName', true);
    }
    case `${NAME_CHANGE}_SUCCESS`: {
      return pipe(
        (s) => s.set('savingName', false),
        (s) => {
          const { id, name } = action.payload;
          const foundIndex = state
            .get('snapshots')
            .findIndex((snapshot) => snapshot.get('id') === id);
          if (foundIndex > -1) {
            return s.updateIn(['snapshots', foundIndex], (snapshot) =>
              snapshot.set('name', name),
            );
          }
          return s;
        },
      )(state);
    }
    case `${NAME_CHANGE}_ERROR`: {
      return state.set('savingName', false);
    }
    case `${NOTE_CHANGE}_START`: {
      return state.set('savingNote', true);
    }
    case `${NOTE_CHANGE}_SUCCESS`: {
      return pipe(
        (s) => s.set('savingNote', false),
        (s) => {
          const { id, note } = action.payload;
          const foundIndex = state
            .get('snapshots')
            .findIndex((snapshot) => snapshot.get('id') === id);
          if (foundIndex > -1) {
            return s.updateIn(['snapshots', foundIndex], (snapshot) =>
              snapshot.set('note', note),
            );
          }
          return s;
        },
      )(state);
    }
    case `${NOTE_CHANGE}_ERROR`: {
      return state.set('savingNote', false);
    }
    case `${LOCK_SNAPSHOT}_START`:
      return state.update('lockingSnapshot', (lockingSnapshot) =>
        lockingSnapshot.push(action.payload.id),
      );
    case `${LOCK_SNAPSHOT}_SUCCESS`: {
      const { id } = action.payload;
      return pipe(
        (s) =>
          s.update('lockingSnapshot', (items) => removeFromList(id, items)),
        (s) => {
          const foundIndex = state
            .get('snapshots')
            .findIndex((snapshot) => snapshot.get('id') === id);
          if (foundIndex > -1) {
            return s.updateIn(['snapshots', foundIndex], (snapshot) =>
              snapshot.set('locked', true),
            );
          }
          return s;
        },
      )(state);
    }
    case `${LOCK_SNAPSHOT}_ERROR`:
      return state.update('lockingSnapshot', (items) =>
        removeFromList(action.payload.id, items),
      );
    case `${UNLOCK_SNAPSHOT}_START`:
      return state.update('unlockingSnapshot', (unlockingSnapshot) =>
        unlockingSnapshot.push(action.payload.id),
      );
    case `${UNLOCK_SNAPSHOT}_SUCCESS`: {
      const { id } = action.payload;
      return pipe(
        (s) =>
          s.update('unlockingSnapshot', (items) => removeFromList(id, items)),
        (s) => {
          const foundIndex = state
            .get('snapshots')
            .findIndex((snapshot) => snapshot.get('id') === id);
          if (foundIndex > -1) {
            return s.updateIn(['snapshots', foundIndex], (snapshot) =>
              snapshot.set('locked', false),
            );
          }
          return s;
        },
      )(state);
    }
    case `${UNLOCK_SNAPSHOT}_ERROR`:
      return state.update('unlockingSnapshot', (items) =>
        removeFromList(action.payload.id, items),
      );

    case ENABLE_START_BTN: {
      return state.set('disableStartButton', false);
    }
    case DISABLE_START_BTN: {
      return state.set('disableStartButton', true);
    }
    case `${FETCH_HOSTNAME}_SUCCESS`: {
      return state.set('hostname', fromJS(action.payload.data));
    }
    case CLOSE_HOSTNAME:
      return state.set('hostname', null);
    case `${NEW_DEVICE_SAVED}_SUCCESS`: {
      const devices = state.get('devices');
      const exists = devices.some((d) => d.sn === get(action.payload, 'sn'));
      if (!exists) {
        return state.update('devices', (d) =>
          d.unshift(fromJS(action.payload).set('id', generateGuid())).take(20),
        );
      }
      return state;
    }
    case `${WORKER_LAST_COMMUNICATION}_SUCCESS`: {
      return state.set('inProgressDevices', fromJS(action.payload));
    }
    case `${BANDWIDTH_MSG_RECEIVED}_SUCCESS`: {
      const data = action.payload;
      const list = Array.isArray(data)
        ? state.bandwidth.push(...data)
        : state.bandwidth.push(data);
      return state.set(
        'bandwidth',
        list.size >= 60 ? new List(list.slice(-60)) : list,
      );
    }
    case `${FETCH_LOGS}_START`:
      return state.set('logsLoading', true);
    case `${FETCH_LOGS}_ERROR`:
      return state.set('logsLoading', false);
    case `${FETCH_LOGS}_SUCCESS`: {
      const { worker, netWorker, tasker } = action.payload;
      return state
        .setIn(['messages', 'worker'], new List(worker))
        .setIn(['messages', 'netWorker'], new List(netWorker))
        .setIn(['messages', 'tasker'], new List(tasker))
        .set('logsLoading', false);
    }
    case CLEAR_LOGS:
      return state.set('messages', new Messages()).set('logsLoading', false);
    case `${TASKER_LOGS_RECEIVED}_SUCCESS`:
      return updateStateMessages(action.payload, 'tasker', state);
    case TASKER_CONSOLE_CLEAR:
      return state.setIn(['messages', 'tasker'], new List());
    case `${WORKER_LOGS_RECEIVED}_SUCCESS`:
      return updateStateMessages(action.payload, 'worker', state);
    case WORKER_CONSOLE_CLEAR:
      return state.setIn(['messages', 'worker'], new List());
    case `${NETWORKER_LOGS_RECEIVED}_SUCCESS`:
      return updateStateMessages(action.payload, 'netWorker', state);
    case NETWORKER_CONSOLE_CLEAR:
      return state.setIn(['messages', 'netWorker'], new List());
    case `${SNAPSHOT_INFO_RECEIVED}_SUCCESS`: {
      const {
        vTask,
        licensedDevCount,
        vCollect,
        vTraceCache,
        errors,
        totalDevCount,
      } = action.payload;
      const id = get(vCollect, '_key');
      const foundIndex = state
        .get('snapshots')
        .findIndex((snapshot) => snapshot.get('id') === id);
      if (foundIndex > -1) {
        return pipe(
          (s) => {
            // NIM-2329 infoStatus is emitted in 2 rounds first everything except erros each 2second and then errors summary each 10s and more.
            if (
              [
                vTask,
                vTraceCache,
                licensedDevCount,
                totalDevCount,
                vCollect,
              ].includes(undefined)
            ) {
              return s;
            }
            return s
              .mergeIn(
                ['snapshots', foundIndex, 'vTask'],
                new ImmutableMap(vTask),
              )
              .mergeIn(
                ['snapshots', foundIndex, 'vTraceCache'],
                new ImmutableMap(vTraceCache),
              )
              .setIn(
                ['snapshots', foundIndex, 'licensedDevCount'],
                licensedDevCount,
              )
              .setIn(['snapshots', foundIndex, 'totalDevCount'], totalDevCount)
              .setIn(['snapshots', foundIndex, 'vCollect'], fromJS(vCollect))
              .setIn(
                ['snapshots', foundIndex, 'loading'],
                get(vCollect, 'loading'),
              )
              .setIn(
                ['snapshots', foundIndex, 'status'],
                get(vCollect, 'status'),
              );
          },
          (s) => {
            if (!Array.isArray(errors)) {
              return s;
            }
            if (!errors.length) {
              const currentErrors = state.getIn([
                'snapshots',
                foundIndex,
                'errors',
              ]);
              if (List.isList(currentErrors) && currentErrors.size) {
                return s;
              }
            }
            return s.setIn(['snapshots', foundIndex, 'errors'], fromJS(errors));
          },
        )(state);
      }
      return state;
    }

    case `${SERVER_TS_RECEIVED}_SUCCESS`: {
      const { currentTs, id } = action.payload;
      const foundIndex = state
        .get('snapshots')
        .findIndex((snapshot) => snapshot.get('id') === id);
      if (foundIndex > -1) {
        return state.setIn(['snapshots', foundIndex, 'ts'], currentTs);
      }
      return state;
    }

    case `${SET_SHAPER_SETTINGS}_SUCCESS`:
      return state.set('maxBandwidth', String(action.payload.bandwidth));
    case `${SNAPSHOT_STATUS_RECEIVED}_SUCCESS`: {
      const { vCollect } = action.payload;
      const id = get(vCollect, '_key');
      const foundIndex = state
        .get('snapshots')
        .findIndex((snapshot) => snapshot.get('id') === id);
      if (foundIndex > -1) {
        return state
          .mergeIn(['snapshots', foundIndex, 'vCollect'], fromJS(vCollect))
          .setIn(
            ['snapshots', foundIndex, 'totalDevCount'],
            get(vCollect, 'totalDevCount'),
          )
          .setIn(
            ['snapshots', foundIndex, 'licensedDevCount'],
            get(vCollect, 'licensedDevCount'),
          )
          .setIn(['snapshots', foundIndex, 'loading'], get(vCollect, 'loading'))
          .setIn(['snapshots', foundIndex, 'status'], get(vCollect, 'status'));
      }
      return state;
    }
    case `${DELETE_DEVICE}_START`: {
      const { snapshotId } = action.payload;
      return state.set('deleteDevicesSnapshotId', snapshotId);
    }

    case `${DELETE_DEVICE}_CANCEL`:
    case `${DELETE_DEVICE}_ERROR`: {
      return state.set('deleteDevicesSnapshotId', null);
    }
    case DELETE_DEVICE_FINISH: {
      const { devices, snapshotId } = action.payload;
      if (!devices || !snapshotId) {
        return state;
      }
      return pipe(
        (s) => {
          const snapshots = s.get('snapshots');
          const foundIndex = snapshots.findIndex(
            (snapshot) => snapshot.get('id') === snapshotId,
          );
          if (foundIndex > -1) {
            return s.updateIn(['snapshots', foundIndex], (snapshot) => {
              return snapshot
                .set(
                  'totalDevCount',
                  snapshot.get('totalDevCount') - devices.length,
                )
                .set(
                  'licensedDevCount',
                  snapshot.get('licensedDevCount') - devices.length,
                );
            });
          }
          return s;
        },
        (s) => s.set('deleteDevicesSnapshotId', null),
      )(state);
    }
    case DELETE_DEVICE_STOP: {
      return state.set('deleteDevicesSnapshotId', null);
    }
    case SET_ACTIVE_MENU_TAB:
      return state.set('activeMenuTab', action.payload);
    case CLEAR_TABS:
      return clearTabs(state);
    case LOADING_STARTED: {
      const { id } = action.payload;
      const foundIndex = state
        .get('snapshots')
        .findIndex((snapshot) => snapshot.get('id') === id);
      if (foundIndex > -1) {
        return clearTabs(
          state.updateIn(['snapshots', foundIndex], (snapshot) =>
            snapshot.set('status', 'run').set('loading', true),
          ),
        );
      }
      return state;
    }
    case `${SAVE_DEVICES}_START`: {
      const { loader, snapshot } = action.payload;
      if (loader) {
        return state.setIn(['fetchDevicesLoader', snapshot], true);
      }
      return state;
    }
    case `${SAVE_DEVICES}_SUCCESS`: {
      return state
        .set('devices', fromJS(action.payload.devices))
        .setIn(['fetchDevicesLoader', action.payload.snapshot], false);
    }
    case `${SAVE_DEVICES}_ERROR`: {
      return state.setIn(
        ['fetchDevicesLoader', action.payload.snapshot],
        false,
      );
    }
    case SHOW_REFRESH_LOADER: {
      const { snapshotId, value } = action.payload;
      return state.setIn(['refreshLoader', snapshotId], value);
    }
    case SHOW_ADD_LOADER: {
      const { snapshotId, value } = action.payload;
      return state.setIn(['addLoader', snapshotId], value);
    }
    case `${OPEN_NEW_SETTINGS}_START`:
    case `${OPEN_SETTINGS}_START`:
      return state.set('settingsLoading', true);
    case `${OPEN_NEW_SETTINGS}_SUCCESS`:
    case `${OPEN_SETTINGS}_SUCCESS`:
      return state.set('settingsLoading', false);
    case `${OPEN_SETTINGS}_ERROR`:
    case `${OPEN_NEW_SETTINGS}_ERROR`:
      return state.set('settingsLoading', false);
    case SNAPSHOT_PREVENT_DEVICE_LOAD: {
      const { index } = action.payload;
      return state.setIn(['snapshots', index, 'preventDeviceLoad'], true);
    }
    case `${DISCOVERY_PROCESS_START}_SAVE_REQUESTDATA`: {
      return state.set('requestData', action.payload.requestData);
    }
    case SAVE_INVALID_PASSWORDS: {
      const { credentials, privileges } = action.payload;
      return state.set(
        'invalidPasswords',
        new ImmutableMap({ credentials, privileges }),
      );
    }
    case CLOSE_SETTINGS_NO_PASSWORDS:
      return state.set('invalidPasswords', new ImmutableMap());
    case `${FETCH_SITE_SEPARATION}_SUCCESS`: {
      return state.set('siteSeparation', action.payload.siteSeparation);
    }
    case DISCOVERY_TASKS_CHANGE:
    case `${FETCH_DISCOVERY_TASKS}_SUCCESS`: {
      return state.set('discoveryTasks', action.payload.discoveryTasks);
    }
    case `${FETCH_DISABLED_POST_DISCOVERY_ACTIONS}_SUCCESS`: {
      return state.set(
        'disabledPostDiscoveryActions',
        right(action.payload.disabledPostDiscoveryActions),
      );
    }
    case DISABLED_POST_DISCOVERY_ACTIONS_CHANGE:
      return state.set(
        'disabledPostDiscoveryActions',
        right(action.payload.disabledPostDiscoveryActions),
      );
    case DOWNLOAD_LOG:
      return state.update('downloadingLogKeys', (downloadingLogKeys) =>
        downloadingLogKeys.concat(new List([action.payload.downloadLogKey])),
      );
    case `${DOWNLOAD_LOG}_FINISH`:
      return state.update('downloadingLogKeys', (downloadingLogKeys) =>
        removeFromList(action.payload.downloadLogKey, downloadingLogKeys),
      );
    default:
      return state;
  }
}
